//
// forms/value-extractor.js - get & set values in forms
//

import foreach from '../helpers/object-foreach';
import find_ancestor from '../helpers/find-ancestor';


const allowed_element_types = [ 'INPUT', 'SELECT', 'TEXTAREA' ];


// get_form_data(form_elements)
// ----------------------------------
// Returns a Form Values Object, as per:
// {
//   name : { div: <div>, inputs: [ ], value: <value> },
//   ...
// }
//

function get_form_data(form_elements) {
  var elements_by_name = Array.from(form_elements).reduce(function(accum, el) {
    var name = el.getAttribute('name');
    if (name) {
      if (!accum.hasOwnProperty(name)) {
        var el_div = find_ancestor(el, 'js-form-item');
        accum[name] = { inputs: [ ], div: el_div };
      }
      accum[name].inputs.push(el);
    }
    return accum;
  }, { });

  foreach(elements_by_name, (k, item) => item.value = get_value(item.inputs));

  return elements_by_name;
}


// get_value() - return value given elements representing one form item
// ----------------------------------
// - Take an array of the elements making up A SINGLE FORM ITEM
//   (perhaps one <input>, or perhaps multiple <input type="radio">, etc.)
//
// - Return the value, if any.
//
// - NB. single checkboxes:    returns true or false
//       multiple checkboxes:  returns an array of values (may be an empty array)
//       radios:               returns null if no selection
//
// - Throws if array parameter is not well formed.
//

function _get_value(elements) {
  elements = guard_param__elements('get_value', elements);

  if (elements.length === 1) {
    var el = elements[0];
    if (el.tagName === 'INPUT' && el.getAttribute('type') === 'checkbox') {
      return (el.checked && el.value) || '';
    }

    if (allowed_element_types.includes(el.tagName)) {
      return el.value;
    }

    throw Error('get_value(): unknown element type');
  }

  // Checkboxes (multi), radios
  var non_inputs = elements.filter(el => !allowed_element_types.includes(el.tagName));
  var types = Object.keys(elements.reduce(
    function(accum, el) { accum[el.type] = 0; return accum; },
    {}
  ));
  if (non_inputs.length !== 0 || types.length !== 1) {
    throw Error('get_value(): invalid element set');
  }

  var selected = elements.filter(function (el) { return el.checked; });

  if (types[0] === 'radio') {
    return (selected.length === 1 ? selected[0].value : null);
  }
  else if (types[0] === 'checkbox') {
    return selected.map(function(el) { return el.value; });
  }

  throw Error('get_value(): invalid element set');
}

function get_value(elements) {
  const el = _get_value(elements);
  return typeof el === 'string' ? el.trim() : el;
}


// get_value_div() - return value for a single form item,
//                   given wrapper div for elements
// ----------------------------------

function get_value_div(el_div) {
  return get_value(form_elements_for_div(el_div));
}


// set_value() - set value on elements representing one form item
// ----------------------------------

function set_value(elements, value) {
  elements = guard_param__elements('set_value', elements);

  if (elements.length === 1) {
    var el = elements[0];
    if (el.tagName === 'INPUT' && el.getAttribute('type') === 'checkbox') {
      if (value === "NULL") {
        value = false;
      }
      el.checked = !!value;
    }

    else if (allowed_element_types.includes(el.tagName)) {
      el.value = value;
    }

    else {
      throw Error('set_value(): unknown element type');
    }
  }

  // Checkboxes (multi)
  else if (value.constructor === Array) {
    elements.forEach(function(el) {
      const val = el.getAttribute('value');
      el.checked = val !== null && value.includes(val);
    });
  }

  // Radios
  else {
    var item = elements.filter(el => el.getAttribute('value') === value);
    if (item.length === 1) {
      item[0].checked = true;
    }
  }
}


// set_value_div()
// ----------------------------------

function set_value_div(el_div, value) {
  set_value(form_elements_for_div(el_div), value);
}


// Helpers
// ----------------------------------

function guard_param__elements(method, elements) {
  if (elements.length === undefined) {
    throw Error(method + '(): elements parameter is not array-like');
  }
  if (elements.length === 0) {
    throw Error(method + '(): no elements');
  }
  return Array.from(elements).filter(el => el.tagName !== 'BUTTON');
}

function form_elements_for_div(el_div) {
  const sel = allowed_element_types.join(',').toLowerCase();
  return el_div.querySelectorAll(sel);
}


export {
  get_value,
  get_value_div,
  get_form_data,

  set_value,
  set_value_div,
};

